/**
 * Autosave hook for the slide editor.
 *
 * Behaviour:
 *   - Local state is the source of truth while the user is typing.
 *   - On every change, schedule a save 600ms later (debounced). New
 *     changes within the window reset the timer.
 *   - On blur, save *immediately* (the user has signalled "I'm done with
 *     this field").
 *   - Each save sends ``If-Match: <version>``; on 412, refetch the deck
 *     and surface a "conflict" error so the user knows their write was
 *     refused (rather than silently disappearing).
 *   - When the active slide changes externally (e.g. the user clicks a
 *     different rail item), the local draft resets to the server slide.
 *
 * The caller passes ``deck`` + ``slideIdx`` and we derive everything else.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useUpdateSlide } from "./hooks";
import type { Deck, Slide, SlideUpdatePayload } from "./types";

const AUTOSAVE_DEBOUNCE_MS = 600;

export type SaveState =
  | { status: "idle" }
  | { status: "saving" }
  | { status: "saved" }
  | { status: "conflict"; message: string }
  | { status: "error"; message: string };

export interface UseSlideAutosaveResult {
  /** Draft slide — what the form is currently editing. */
  draft: Slide;
  /** Patch one or more fields and trigger a debounced save. */
  setField<K extends keyof SlideUpdatePayload>(
    key: K,
    value: SlideUpdatePayload[K],
  ): void;
  /** Flush any pending save immediately (call this on blur). */
  flush(): void;
  saveState: SaveState;
}

export function useSlideAutosave(
  deck: Deck,
  slideIdx: number,
): UseSlideAutosaveResult {
  const serverSlide = deck.deck_json.slides[slideIdx];
  const [draft, setDraft] = useState<Slide>(
    serverSlide ?? makeEmptySlide(),
  );
  const [saveState, setSaveState] = useState<SaveState>({ status: "idle" });

  // Reset the draft when the user switches slides or loads a different
  // deck. Deliberately NOT keyed on ``deck.version`` — our own autosaves
  // bump that, and re-syncing from the server on every save would clobber
  // any in-flight typing the user is doing while the previous save
  // round-trips. PR3 onward will introduce a conflict-merge UI for
  // cross-tab editing; until then, the version field on the deck row is
  // already the canonical state.
  const lastSlideKeyRef = useRef<string>("");
  useEffect(() => {
    if (!serverSlide) return;
    const key = `${deck.id}:${slideIdx}`;
    if (lastSlideKeyRef.current !== key) {
      lastSlideKeyRef.current = key;
      setDraft(serverSlide);
      setSaveState({ status: "idle" });
    }
  }, [deck.id, slideIdx, serverSlide]);

  const mutation = useUpdateSlide(deck.id, {
    onMutate: () => {
      setSaveState({ status: "saving" });
    },
    onSuccess: () => {
      setSaveState({ status: "saved" });
    },
    onError: (err: Error) => {
      // The apiClient wraps the response detail with the "API <status>: "
      // prefix — a 412 surfaces as "API 412: …". Treat that as a conflict.
      if (/^API 412/.test(err.message)) {
        setSaveState({
          status: "conflict",
          message:
            "Another save landed between when you opened this slide and now. " +
            "Refresh the deck to see the latest, then re-apply your edit.",
        });
      } else {
        setSaveState({ status: "error", message: err.message });
      }
    },
  });

  // Pending patch + timer — declared as refs so re-renders don't churn
  // them.
  const pendingPatchRef = useRef<SlideUpdatePayload | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const patch = pendingPatchRef.current;
    if (!patch || Object.keys(patch).length === 0) return;
    pendingPatchRef.current = null;
    mutation.mutate({
      slideIdx,
      payload: patch,
      ifMatchVersion: deck.version,
    });
  }, [deck.version, mutation, slideIdx]);

  const scheduleSave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      flush();
    }, AUTOSAVE_DEBOUNCE_MS);
  }, [flush]);

  const setField = useCallback(
    <K extends keyof SlideUpdatePayload>(
      key: K,
      value: SlideUpdatePayload[K],
    ): void => {
      setDraft((d) => ({ ...d, [key]: value }) as Slide);
      pendingPatchRef.current = {
        ...(pendingPatchRef.current ?? {}),
        [key]: value,
      } as SlideUpdatePayload;
      scheduleSave();
    },
    [scheduleSave],
  );

  // Flush any pending save before the component unmounts so quick
  // slide-switches don't drop edits on the floor.
  useEffect(() => {
    return () => {
      if (pendingPatchRef.current) {
        flush();
      } else if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [flush]);

  return { draft, setField, flush, saveState };
}

function makeEmptySlide(): Slide {
  return {
    section: "",
    slug: "",
    titleHtml: "",
    meta: false,
    bodyHtml: "",
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
  };
}
